import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { marketingApi, type MarketingDashboard } from '@/api/marketing.api';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { Button } from '@/components/ui/Button';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { ChartCard } from '@/components/ui/ChartCard';
import { PageSection } from '@/components/ui/PageSection';
import { QuickActionGrid } from '@/components/ui/QuickActionGrid';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmailIcon from '@mui/icons-material/Email';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { showApiError } from '@/utils/apiError';

const QUICK_LINKS = [
  { to: '/marketing/segments', label: 'Audiences', description: 'Segments and targeting rules' },
  { to: '/marketing/templates', label: 'Templates', description: 'Email and message templates' },
  { to: '/marketing/journeys', label: 'Journeys', description: 'Automated lifecycle flows' },
  { to: '/marketing/lifecycle', label: 'Lifecycle', description: 'Stage-based engagement' },
  { to: '/marketing/reports', label: 'Reports', description: 'Campaign performance exports' },
  { to: '/marketing/webhooks', label: 'ESP webhooks', description: 'Delivery and bounce events' },
];

export function MarketingDashboardPage() {
  const [data, setData] = useState<MarketingDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await marketingApi.getDashboard());
    } catch (err) {
      showApiError(err, 'Failed to load marketing dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const funnelData = data
    ? [
        { stage: 'Sent', count: data.totalSent },
        { stage: 'Opens', count: data.totalOpens },
        { stage: 'Clicks', count: data.totalClicks },
        { stage: 'Conversions', count: data.totalConversions },
      ]
    : [];

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="Marketing"
        title="Marketing"
        description="Campaigns, audiences, journeys, and attribution"
      >
        <DashboardSkeleton kpiCount={4} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      moduleLabel="Marketing"
      title="Marketing"
      description="Campaigns, audiences, journeys, and attribution — integrated with CRM, POS, and lead generation"
      actions={
        <Link to="/marketing/campaigns">
          <Button size="sm">New campaign</Button>
        </Link>
      }
    >
      {data && (
        <>
          <KpiGrid columns={4}>
            <StatCard title="Campaigns" value={data.totalCampaigns} icon={<CampaignIcon />} />
            <StatCard title="Emails sent" value={data.totalSent} icon={<EmailIcon />} />
            <StatCard title="Open rate" value={`${(data.openRate * 100).toFixed(1)}%`} icon={<TouchAppIcon />} />
            <StatCard title="Conversions" value={data.totalConversions} icon={<TrendingUpIcon />} />
          </KpiGrid>

          <div className="grid gap-6 lg:grid-cols-2">
            <ChartCard title="Engagement funnel" description="Sent → opens → clicks → conversions">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={funnelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
                  <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <section className="card p-5">
              <h3 className="section-title">Top campaigns</h3>
              <ul className="mt-4 space-y-2 text-sm">
                {(data.topCampaigns ?? []).map((c) => (
                  <li key={c.campaignId} className="flex justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                    <span className="text-slate-800 dark:text-slate-200">{c.name ?? `Campaign #${c.campaignId}`}</span>
                    <span className="text-slate-500">{c.opens} opens</span>
                  </li>
                ))}
                {(data.topCampaigns ?? []).length === 0 && (
                  <li className="text-slate-500">Launch a campaign to see performance here.</li>
                )}
              </ul>
            </section>
          </div>

          <PageSection title="Quick actions">
            <QuickActionGrid items={QUICK_LINKS} columns={3} />
          </PageSection>
        </>
      )}
    </DashboardLayout>
  );
}
